import React, { AnimationEvent, PureComponent, ReactNode } from 'react'
import StyledDiv from './styles'

export type SlideDirection = 'forward' | 'back'

interface ISlidePanel {
  key: string
  animation?: string
}

interface IProps {
  panelKey: string
  getDirection: (previousKey: string, nextKey: string) => SlideDirection
  renderPanel: (key: string) => ReactNode
  className?: string
}

interface IState {
  panels: ISlidePanel[]
}

const prefersReducedMotion = (): boolean => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export class SlideTransition extends PureComponent<IProps, IState> {
  private currentPanelRef = React.createRef<HTMLDivElement>()

  constructor(props: IProps) {
    super(props)
    this.state = { panels: [{ key: props.panelKey }] }
  }

  public componentDidUpdate(prevProps: IProps): void {
    const { panelKey, getDirection } = this.props
    if (prevProps.panelKey === panelKey) return

    if (prefersReducedMotion()) {
      this.setState({ panels: [{ key: panelKey }] }, this.focusCurrentPanel)
      return
    }

    const direction = getDirection(prevProps.panelKey, panelKey)
    this.setState({
      panels: [
        { key: prevProps.panelKey, animation: `leave-${ direction }` },
        { key: panelKey, animation: `enter-${ direction }` }
      ]
    }, this.focusCurrentPanel)
  }

  public render(): ReactNode {
    const { panelKey, renderPanel, className } = this.props
    const { panels } = this.state
    const isSliding = panels.length > 1

    return (
      <StyledDiv className={ `${ className ?? '' } ${ isSliding ? 'sliding' : '' }` }>
        { panels.map(panel => {
          const isCurrent = panel.key === panelKey
          return (
            <div
              key={ panel.key }
              ref={ isCurrent ? this.currentPanelRef : undefined }
              className={ `slide-panel ${ panel.animation ?? '' }` }
              aria-hidden={ isCurrent ? undefined : true }
              tabIndex={ isCurrent ? -1 : undefined }
              onAnimationEnd={ isCurrent ? undefined : this.handleLeaveEnd }
            >
              { renderPanel(panel.key) }
            </div>
          )
        }) }
      </StyledDiv>
    )
  }

  private handleLeaveEnd = (event: AnimationEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return

    this.setState({ panels: [{ key: this.props.panelKey }] })
  }

  private focusCurrentPanel = (): void => {
    this.currentPanelRef.current?.focus({ preventScroll: true })
  }
}
